import { useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Sparkles, Crown, Trophy } from 'lucide-react'
import { useAchievementModal } from '../../stores/loyaltyStore'
import { Button } from '../ui'
import AchievementBadge from './AchievementBadge'
import { useAccessibility } from '../../hooks/useAccessibility'

interface ParticleProps {
  delay: number
  duration: number
  fromX: number
  fromY: number
  toX: number
  toY: number
  color: string
}

const Particle = ({ delay, duration, fromX, fromY, toX, toY, color }: ParticleProps) => (
  <motion.div
    className="absolute w-2 h-2 rounded-full"
    style={{ backgroundColor: color }}
    initial={{
      x: fromX,
      y: fromY,
      scale: 0,
      opacity: 1
    }}
    animate={{
      x: toX,
      y: toY,
      scale: [0, 1, 0],
      opacity: [0, 1, 0]
    }}
    transition={{
      duration,
      delay,
      ease: "easeOut"
    }}
  />
)

const AchievementModal = () => {
  const { isOpen, achievement, onClose } = useAchievementModal()
  const modalRef = useRef<HTMLDivElement>(null)
  const { announce, createFocusTrap } = useAccessibility()

  // Focus management and accessibility
  useEffect(() => {
    if (isOpen && achievement) {
      // Announce achievement for screen readers
      announce(
        `¡Felicidades! Has desbloqueado el logro ${achievement.name}. ${achievement.description}`,
        'assertive'
      )

      // Set up focus trap
      let cleanup: (() => void) | undefined
      if (modalRef.current) {
        cleanup = createFocusTrap(modalRef.current)
      }

      return cleanup
    }
  }, [isOpen, achievement, announce, createFocusTrap])

  if (!isOpen || !achievement) return null

  // Generate particles for celebration effect
  const generateParticles = () => {
    const particles = []
    const centerX = 250
    const centerY = 200
    const colors = [
      achievement.frameColor,
      '#FFD700',
      '#FF6B6B',
      '#4ECDC4',
      '#45B7D1',
      '#96CEB4'
    ]

    for (let i = 0; i < 30; i++) {
      const angle = (i / 30) * Math.PI * 2
      const distance = 100 + Math.random() * 100
      const toX = centerX + Math.cos(angle) * distance
      const toY = centerY + Math.sin(angle) * distance

      particles.push(
        <Particle
          key={i}
          delay={i * 0.05}
          duration={1 + Math.random() * 0.5}
          fromX={centerX}
          fromY={centerY}
          toX={toX}
          toY={toY}
          color={colors[i % colors.length]}
        />
      )
    }

    return particles
  }

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Backdrop */}
        <motion.div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        />

        {/* Modal Container */}
        <div className="flex items-center justify-center min-h-screen p-4">
          <motion.div
            ref={modalRef}
            className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
            initial={{ scale: 0.5, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.5, opacity: 0, y: 50 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 25
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="achievement-title"
            aria-describedby="achievement-description"
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-20 p-2 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white transition-colors shadow-lg"
              aria-label="Cerrar modal de logro"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>

            {/* Background Gradient */}
            <motion.div
              className="absolute inset-0 opacity-20"
              style={{
                background: `linear-gradient(135deg, ${achievement.frameColor}40, ${achievement.frameColor}10)`
              }}
              animate={{
                background: [
                  `linear-gradient(135deg, ${achievement.frameColor}40, ${achievement.frameColor}10)`,
                  `linear-gradient(180deg, ${achievement.frameColor}30, ${achievement.frameColor}20)`,
                  `linear-gradient(225deg, ${achievement.frameColor}40, ${achievement.frameColor}10)`
                ]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />

            {/* Celebration Particles */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              {generateParticles()}
            </div>

            {/* Content */}
            <div className="relative z-10 p-8 text-center">
              {/* Top Icon */}
              <motion.div
                className="mb-6"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 200,
                  damping: 15,
                  delay: 0.2
                }}
              >
                <div className="relative mx-auto w-20 h-20 mb-4">
                  <motion.div
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: `linear-gradient(45deg, ${achievement.frameColor}, ${achievement.frameColor}80)`
                    }}
                    animate={{
                      scale: [1, 1.1, 1],
                      rotate: [0, 5, -5, 0]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  />
                  <Crown className="absolute inset-0 m-auto w-10 h-10 text-white" />
                </div>
              </motion.div>

              {/* Achievement Badge */}
              <motion.div
                className="mb-6"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 200,
                  damping: 20,
                  delay: 0.4
                }}
              >
                <AchievementBadge
                  level={achievement.level}
                  size="xl"
                  showTooltip={false}
                  className="mx-auto"
                />
              </motion.div>

              {/* Title */}
              <motion.h2
                id="achievement-title"
                className="text-3xl font-bold text-gray-900 mb-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                ¡Logro Desbloqueado!
              </motion.h2>

              {/* Achievement Name */}
              <motion.h3
                className="text-2xl font-bold mb-2"
                style={{ color: achievement.frameColor }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                {achievement.name}
              </motion.h3>

              {/* Description */}
              <motion.p
                id="achievement-description"
                className="text-gray-600 mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                {achievement.description}
              </motion.p>

              {/* Points Required */}
              <motion.div
                className="flex items-center justify-center gap-2 mb-6"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.9 }}
              >
                <Sparkles className="w-5 h-5 text-yellow-500" />
                <span className="text-lg font-bold text-gray-700">
                  {achievement.pointsRequired} puntos alcanzados
                </span>
                <Sparkles className="w-5 h-5 text-yellow-500" />
              </motion.div>

              {/* Celebration Message */}
              <motion.div
                className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-4 mb-6 border border-green-200"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1 }}
              >
                <Trophy className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="text-green-800 font-medium">
                  {achievement.celebrationMessage}
                </p>
              </motion.div>

              {/* Action Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1 }}
              >
                <Button
                  onClick={onClose}
                  variant="premium"
                  size="lg"
                  className="w-full"
                  style={{
                    background: `linear-gradient(135deg, ${achievement.frameColor}, ${achievement.frameColor}dd)`
                  }}
                >
                  ¡Increíble!
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Screen Reader Announcement */}
        <div
          className="sr-only"
          aria-live="assertive"
          aria-atomic="true"
        >
          Has desbloqueado el logro {achievement.name}: {achievement.description}.
          Has alcanzado {achievement.pointsRequired} puntos de lealtad.
          {achievement.celebrationMessage}
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

export default AchievementModal